import AudioContextProvider from '../../../libraries/audio/AudioContextProvider'

class PlayAudioManager {
  audioQueue: Int16Array[] = []
  isPlaying: boolean = false
  sampleRate: number = 24000
  audioContext: AudioContext | null = null
  allAudioReceived: boolean = false

  // Function to set the sample rate
  setSampleRate(rate: number): void {
    this.sampleRate = rate
  }

  // Mark that all audio chunks have been received
  markAllAudioReceived(): void {
    this.allAudioReceived = true
    this.checkPlaybackComplete()
  }

  // Reset state for new audio stream
  reset(): void {
    this.audioQueue = []
    this.allAudioReceived = false
  }

  play(): void {
    this.playNext() // Start playing if not already playing
  }

  // Check if playback is complete and trigger callback
  private checkPlaybackComplete(): void {
    if (this.allAudioReceived && !this.isPlaying && this.audioQueue.length === 0) {
      // Suspend shared context to save resources; provider keeps it for reuse
      AudioContextProvider.suspend(this.sampleRate)
    }
  }

  // Function to decode base64 PCM16 audio to Int16Array
  decodeBase64ToInt16Array(base64: string): Int16Array {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return new Int16Array(bytes.buffer)
  }

  // Function to add audio data to the queue
  addAudioToQueue(audioData: string | Int16Array): void {
    const int16Array = typeof audioData === 'string'
      ? this.decodeBase64ToInt16Array(audioData)
      : audioData
    this.audioQueue.push(int16Array)
  }

  // Function to play the next audio chunk in the queue
  async playNext(): Promise<void> {
    if (this.isPlaying || this.audioQueue.length === 0) {
      if (!this.isPlaying && this.audioQueue.length === 0) {
        this.checkPlaybackComplete()
      }
      return
    }

    this.isPlaying = true

    const audioData = this.audioQueue.shift() // Get the next audio chunk
    if (audioData) {
      await this.playAudio(audioData) // Play the audio
    }

    this.isPlaying = false
    this.playNext() // Play the next audio in the queue
  }

  // Function to play a single audio chunk
  playAudio(audioBuffer: Int16Array): Promise<void> {
    console.log('🔈 Playing audio chunk...')
    return new Promise((resolve, reject) => {
      try {
        const audioContext = AudioContextProvider.getAudioContext(this.sampleRate)

        const startPlayback = () => {
          // Convert Int16Array (PCM16) to Float32Array
          const float32Array = new Float32Array(audioBuffer.length)
          for (let i = 0; i < audioBuffer.length; i++) {
            float32Array[i] = audioBuffer[i] / 32768.0 // Normalize to -1.0 to 1.0
          }

          // Create an AudioBuffer with the correct sample rate
          const audioBufferObj = audioContext.createBuffer(1, float32Array.length, this.sampleRate)
          audioBufferObj.copyToChannel(float32Array, 0) // Copy PCM data to the buffer

          // Create a BufferSource to play the audio
          const source = audioContext.createBufferSource()
          source.buffer = audioBufferObj
          source.connect(audioContext.destination)

          source.onended = () => {
            resolve() // Resolve when the playback ends
          }

          source.start(0) // Start playback
        }

        if (audioContext.state !== 'running') {
          AudioContextProvider.resume(this.sampleRate).then(startPlayback)
        } else {
          startPlayback()
        }
      } catch(e: unknown) {
        if(e instanceof Error) {
          console.error('Error playing audio', e.message)
        }
        console.error('Unknown error playing audio')
        reject(e)
      }
    })
  }
}

export default PlayAudioManager
